import React, { useEffect, useState } from 'react';
import { Plus, Search, Edit, Trash2, X, Calendar, DollarSign, User, CheckCircle, XCircle, Send } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface AirplaneReimbursement {
  id: string;
  driver_name: string;
  date: string;
  airline_company: string;
  amount: number;
  who_pays: 'company' | 'driver';
  driver_paid: boolean;
  created_at: string;
}

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
}

export function AirplaneReimbursementsView() {
  const toast = useToast();
  const [reimbursements, setReimbursements] = useState<AirplaneReimbursement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReimbursement, setSelectedReimbursement] = useState<AirplaneReimbursement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reimbursementToDelete, setReimbursementToDelete] = useState<AirplaneReimbursement | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);

  const [formData, setFormData] = useState({
    driver_name: '',
    date: new Date().toISOString().split('T')[0],
    airline_company: '',
    amount: '',
    who_pays: 'company' as 'company' | 'driver',
    driver_paid: false,
  });

  useEffect(() => {
    loadReimbursements();
    loadDrivers();
  }, []);

  async function loadDrivers() {
    try {
      // Load all drivers including unassigned/inactive ones
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, driver_status')
        .order('first_name');

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  }

  async function loadReimbursements() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('airplane_reimbursements')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;

      setReimbursements(data || []);
    } catch (error) {
      console.error('Error loading airplane reimbursements:', error);
      toast.error('Failed to load airplane reimbursements');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.driver_name || !formData.date || !formData.airline_company || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('airplane_reimbursements')
        .insert([{
          driver_name: formData.driver_name.trim(),
          date: formData.date,
          airline_company: formData.airline_company.trim(),
          amount: parseFloat(formData.amount),
          who_pays: formData.who_pays,
          driver_paid: formData.driver_paid,
        }]);

      if (error) throw error;

      toast.success('Airplane ticket reimbursement added successfully!');
      setShowAddModal(false);
      resetForm();
      loadReimbursements();
    } catch (error) {
      console.error('Error adding airplane reimbursement:', error);
      toast.error('Failed to add airplane reimbursement');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedReimbursement) return;

    try {
      const { error } = await supabase
        .from('airplane_reimbursements')
        .update({
          driver_name: formData.driver_name.trim(),
          date: formData.date,
          airline_company: formData.airline_company.trim(),
          amount: parseFloat(formData.amount),
          who_pays: formData.who_pays,
          driver_paid: formData.driver_paid,
        })
        .eq('id', selectedReimbursement.id);

      if (error) throw error;

      toast.success('Airplane ticket reimbursement updated successfully!');
      setShowEditModal(false);
      setSelectedReimbursement(null);
      resetForm();
      loadReimbursements();
    } catch (error) {
      console.error('Error updating airplane reimbursement:', error);
      toast.error('Failed to update airplane reimbursement');
    }
  };

  const handleDelete = async () => {
    if (!reimbursementToDelete) return;

    try {
      const { error } = await supabase
        .from('airplane_reimbursements')
        .delete()
        .eq('id', reimbursementToDelete.id);

      if (error) throw error;

      toast.success('Airplane ticket reimbursement deleted successfully!');
      setShowDeleteConfirm(false);
      setReimbursementToDelete(null);
      loadReimbursements();
    } catch (error) {
      console.error('Error deleting airplane reimbursement:', error);
      toast.error('Failed to delete airplane reimbursement');
    }
  };

  const resetForm = () => {
    setFormData({
      driver_name: '',
      date: new Date().toISOString().split('T')[0],
      airline_company: '',
      amount: '',
      who_pays: 'company',
      driver_paid: false,
    });
  };

  const openEditModal = (reimbursement: AirplaneReimbursement) => {
    setSelectedReimbursement(reimbursement);
    setFormData({
      driver_name: reimbursement.driver_name,
      date: reimbursement.date,
      airline_company: reimbursement.airline_company,
      amount: reimbursement.amount.toString(),
      who_pays: reimbursement.who_pays,
      driver_paid: reimbursement.driver_paid,
    });
    setShowEditModal(true);
  };

  const toggleDriverPaid = async (reimbursement: AirplaneReimbursement) => {
    if (reimbursement.who_pays !== 'driver') return;

    try {
      const { error } = await supabase
        .from('airplane_reimbursements')
        .update({ driver_paid: !reimbursement.driver_paid })
        .eq('id', reimbursement.id);

      if (error) throw error;

      toast.success(`Payment status updated to ${!reimbursement.driver_paid ? 'Paid' : 'Not Paid'}`);
      loadReimbursements();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const filteredReimbursements = reimbursements.filter(reimbursement => {
    const searchLower = searchTerm.toLowerCase();
    return (
      reimbursement.driver_name.toLowerCase().includes(searchLower) ||
      reimbursement.airline_company.toLowerCase().includes(searchLower) ||
      reimbursement.date.includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-none border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Airplane Tickets Reimbursements</h1>
            <p className="text-sm text-gray-500 mt-1">Manage airplane ticket expenses and reimbursements</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Airplane Ticket
          </button>
        </div>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by driver name, airline, or date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Driver
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Airline Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Who Pays
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Driver Paid
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredReimbursements.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  <Send className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-lg font-medium">No airplane ticket reimbursements found</p>
                  <p className="text-sm mt-1">Add your first airplane ticket reimbursement to get started</p>
                </td>
              </tr>
            ) : (
              filteredReimbursements.map((reimbursement) => {
                const getRowColor = () => {
                  if (reimbursement.who_pays === 'company') return 'bg-white hover:bg-gray-50';
                  if (reimbursement.driver_paid) return 'bg-green-50 hover:bg-green-100';
                  return 'bg-red-50 hover:bg-red-100';
                };
                
                return (
                <tr key={reimbursement.id} className={getRowColor()}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{reimbursement.driver_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">
                        {new Date(reimbursement.date).toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{reimbursement.airline_company}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {reimbursement.amount.toFixed(2)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      reimbursement.who_pays === 'company'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {reimbursement.who_pays === 'company' ? 'Company' : 'Driver'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {reimbursement.who_pays === 'driver' ? (
                      <div className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={reimbursement.driver_paid}
                          onChange={() => toggleDriverPaid(reimbursement)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        />
                        {reimbursement.driver_paid ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(reimbursement)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setReimbursementToDelete(reimbursement);
                        setShowDeleteConfirm(true);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Add Airplane Ticket Reimbursement</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Driver <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={driverSearch}
                    onChange={(e) => { setDriverSearch(e.target.value); setShowDriverDropdown(true); }}
                    onFocus={() => setShowDriverDropdown(true)}
                    placeholder="Search driver..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {driverSearch && showDriverDropdown && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
                    {drivers
                      .filter(d =>
                        `${d.first_name} ${d.last_name}`.toLowerCase().includes(driverSearch.toLowerCase())
                      )
                      .map((driver) => (
                        <button
                          key={driver.id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, driver_name: `${driver.first_name} ${driver.last_name}` });
                            setDriverSearch(`${driver.first_name} ${driver.last_name}`);
                            setShowDriverDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                        >
                          {driver.first_name} {driver.last_name}
                        </button>
                      ))}
                  </div>
                )}
                {formData.driver_name && !driverSearch && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Driver selected: {formData.driver_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Airline Company <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.airline_company}
                  onChange={(e) => setFormData({ ...formData, airline_company: e.target.value })}
                  placeholder="e.g., American Airlines, Delta, United"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Who Pays <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.who_pays}
                  onChange={(e) => setFormData({ ...formData, who_pays: e.target.value as 'company' | 'driver' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="company">Company</option>
                  <option value="driver">Driver</option>
                </select>
              </div>

              {formData.who_pays === 'driver' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="driver_paid"
                    checked={formData.driver_paid}
                    onChange={(e) => setFormData({ ...formData, driver_paid: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="driver_paid" className="ml-2 text-sm text-gray-700">
                    Driver has paid
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Reimbursement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedReimbursement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Edit Airplane Ticket Reimbursement</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedReimbursement(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Driver Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.driver_name}
                  onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                  placeholder="Enter driver name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Airline Company <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.airline_company}
                  onChange={(e) => setFormData({ ...formData, airline_company: e.target.value })}
                  placeholder="e.g., American Airlines, Delta, United"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Who Pays <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.who_pays}
                  onChange={(e) => setFormData({ ...formData, who_pays: e.target.value as 'company' | 'driver' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="company">Company</option>
                  <option value="driver">Driver</option>
                </select>
              </div>

              {formData.who_pays === 'driver' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="driver_paid_edit"
                    checked={formData.driver_paid}
                    onChange={(e) => setFormData({ ...formData, driver_paid: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="driver_paid_edit" className="ml-2 text-sm text-gray-700">
                    Driver has paid
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedReimbursement(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Update Reimbursement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && reimbursementToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Airplane Ticket Reimbursement</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this airplane ticket reimbursement for{' '}
              <span className="font-semibold">{reimbursementToDelete.driver_name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setReimbursementToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
